import React, { useState } from "react";
import { ExternalLink, ShoppingBag, Tag } from "lucide-react";
import PageLayout from "../ui/PageLayout";
import PageHero from "../ui/PageHero";
import FadeIn from "../ui/FadeIn";


const products = [
  {
    name: "The Spark Within: True Stories of Ordinary People Igniting Extraordinary Social Change",
    price: "₹456.00",
    originalPrice: "₹500.00",
    img: "https://twagaa.com/wp-content/uploads/2025/06/TSW_05A.jpg",
    link: "https://twagaa.com/product/tsw/",
    category: "Books",
    badge: "Bestseller",
    badgeColor: "bg-amber-100 text-amber-800",
  },
  {
    name: "T-shirt – Kindness is a Choice – For Men",
    price: "₹800.00 – ₹960.00",
    img: "https://twagaa.com/wp-content/uploads/2025/06/KU-KIAC-flower-1-a17d98-black-men.jpg",
    link: "https://twagaa.com/product/t-shirt-kindness-is-a-choice-for-men/",
    category: "Clothing",
    badge: null,
  },
  {
    name: "T-shirt – Kindness is a Choice – For Women",
    price: "₹800.00 – ₹880.00",
    img: "https://twagaa.com/wp-content/uploads/2025/06/KU-KIAC-flower-1-a17d98-white-women-600x779.jpg",
    link: "https://twagaa.com/product/t-shirt-kindness-is-a-choice-for-women/",
    category: "Clothing",
    badge: null,
  },
  {
    name: "T-Shirt (Polo Unisex) – Kindness Is A Choice",
    price: "₹1200.00",
    originalPrice: "₹1500.00",
    img: "https://twagaa.com/wp-content/uploads/2025/06/KU-KIAC-MEN-POLO-FRONT-flower-1-a17d98-PURPLE-600x750.jpg",
    link: "https://twagaa.com/product/t-shirt-polo-kindness-is-a-choice/",
    category: "Clothing",
    badge: "20% Off",
    badgeColor: "bg-green-100 text-green-800",
  },
  {
    name: "Kids T-shirt – Kindness is a Choice",
    price: "₹725.00",
    originalPrice: "₹895.00",
    img: "https://twagaa.com/wp-content/uploads/2025/06/KU-KIAC-flower-1-a17d98-white-kids-600x779.jpg",
    link: "https://twagaa.com/product/kids-t-shirt-kindness-is-a-choice-for-kids/",
    category: "Clothing",
    badge: "Popular",
    badgeColor: "bg-ku-soft text-ku-royal",
  },
];

export default function KindnessShop() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const categories = ["All", "Books", "Clothing"];
  const filteredProducts =
    selectedCategory === "All" ? products : products.filter((p) => p.category === selectedCategory);

  return (
    <PageLayout>
      <PageHero
        title="KU Merchandise"
        eyebrow="Kindness Shop"
        subtitle="Wear your kindness. Support the movement. Every purchase fuels our social impact mission."
        light
      />

      {/* Category Info Banner */}
      <section className="py-6 bg-white border-b border-ku-soft/60">
        <div className="ku-container flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 font-body">
            <ShoppingBag className="w-4 h-4 text-ku-violet" />
            <span>Sold via <a href="https://twagaa.com" target="_blank" rel="noopener noreferrer" className="text-ku-violet font-semibold hover:underline">Twagaa.com</a> — our trusted merchandise partner</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full font-accent text-sm font-semibold transition-all min-h-[36px] ${
                  selectedCategory === cat
                    ? "bg-ku-royal text-white shadow-card"
                    : "bg-ku-soft text-ku-royal hover:bg-ku-lavender/40"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <p className="text-gray-500 font-body text-sm">{filteredProducts.length} products</p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="ku-section bg-ku-cream">
        <div className="ku-container">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filteredProducts.map((product, index) => (
              <FadeIn key={product.name} delay={index * 0.06}>
                <a
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group ku-card overflow-hidden block h-full flex flex-col"
                >
                  {/* Image */}
                  <div className="relative aspect-[3/4] overflow-hidden bg-ku-soft flex-shrink-0">
                    <img
                      src={product.img}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-ku-royal/0 group-hover:bg-ku-royal/10 transition-colors duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full px-4 py-2 flex items-center gap-2 text-ku-royal font-accent font-semibold text-sm shadow-elevated">
                        <ExternalLink className="w-4 h-4" />
                        View Product
                      </div>
                    </div>
                    {/* Badge */}
                    {product.badge && (
                      <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-accent font-bold ${product.badgeColor}`}>
                        {product.badge}
                      </div>
                    )}
                    {/* Category tag */}
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs font-accent font-semibold text-ku-royal">
                      {product.category}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-5 flex flex-col flex-1">
                    <h2 className="font-display font-bold text-ku-royal mb-3 line-clamp-3 text-sm md:text-base flex-1">
                      {product.name}
                    </h2>
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-ku-soft/60">
                      <div className="flex items-center gap-2">
                        {product.originalPrice && (
                          <span className="text-gray-400 line-through text-xs font-body">{product.originalPrice}</span>
                        )}
                        <span className="font-accent font-bold text-ku-royal text-base">{product.price}</span>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-accent font-semibold text-ku-violet group-hover:text-ku-royal transition-colors">
                        Shop <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </a>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
